// eslint-disable-next-line import/no-extraneous-dependencies
import dagre from 'dagre';
import { MarkerType } from 'reactflow';
import { NODE_TYPES, SIGNALS, COLORS } from '../constants';

const nodeWidth = 250;
const nodeHeight = 100;

export const DEFAULT_EDGE_COLOR = '#a855f7';
const DIMMED_EDGE_COLOR = '#e2e8f0';

export function normalizeEdgesForDraggable(edges) {
  return edges.map(e => ({
    ...e,
    type: 'draggable',
    data: {
      ...e.data,
      originalColor:
        e.style?.stroke || e.data?.originalColor || DEFAULT_EDGE_COLOR,
    },
    style: { ...e.style, strokeWidth: 2 },
    pathOptions: { borderRadius: 20 },
    markerMid: e.markerMid
      ? { ...e.markerMid, width: 20, height: 20 }
      : undefined,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: e.style?.stroke || e.markerEnd?.color || DEFAULT_EDGE_COLOR,
    },
  }));
}

/** Highlight edges connected to the selected node so the path is visible; dim others. */
export function applyEdgeStyles(edges, { highlightedNodeId = null } = {}) {
  return edges.map(edge => {
    const originalColor =
      edge.data?.originalColor || edge.style?.stroke || DEFAULT_EDGE_COLOR;
    const isConnected =
      highlightedNodeId &&
      (edge.source === highlightedNodeId || edge.target === highlightedNodeId);
    const markerSize = { width: 20, height: 20 };
    if (isConnected) {
      return {
        ...edge,
        animated: true,
        style: { ...edge.style, stroke: originalColor, strokeWidth: 3 },
        markerEnd: {
          ...edge.markerEnd,
          ...markerSize,
          color: originalColor,
        },
        markerMid: edge.markerMid
          ? { ...edge.markerMid, ...markerSize, color: originalColor }
          : {
              type: edge.markerEnd?.type,
              ...markerSize,
              color: originalColor,
            },
        zIndex: 1000,
      };
    }
    return {
      ...edge,
      animated: true,
      style: {
        ...edge.style,
        stroke: highlightedNodeId ? DIMMED_EDGE_COLOR : originalColor,
        strokeWidth: highlightedNodeId ? 1 : 2,
      },
      markerEnd: {
        ...edge.markerEnd,
        ...markerSize,
        color: highlightedNodeId ? DIMMED_EDGE_COLOR : originalColor,
      },
      markerMid: edge.markerMid
        ? {
            ...edge.markerMid,
            ...markerSize,
            color: highlightedNodeId ? DIMMED_EDGE_COLOR : originalColor,
          }
        : {
            type: edge.markerEnd?.type,
            ...markerSize,
            color: highlightedNodeId ? DIMMED_EDGE_COLOR : originalColor,
          },
      zIndex: 0,
    };
  });
}

/** Single source of truth for node subtitle text. */
export function getNodeSubtitle(nodeType) {
  switch (nodeType) {
    case NODE_TYPES.TRIGGER:
      return 'Start of flow';
    case NODE_TYPES.SWITCH:
      return '(Split Based On...)';
    case NODE_TYPES.SEND_MESSAGE:
      return '(Send message)';
    case NODE_TYPES.SEND_MESSAGE_AND_WAIT:
      return '(Send and wait for reply)';
    case NODE_TYPES.MAKE_HTTP_REQUEST:
      return '(External Request)';
    default:
      return 'Start of flow';
  }
}

export const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });
  nodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });
  dagre.layout(dagreGraph);
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });
  return { nodes: layoutedNodes, edges };
};

export const getNodeExitPoints = node => {
  const exits = [];
  if (!node.routes) return exits;

  node.routes.forEach((route, index) => {
    if (route.criteria) {
      route.criteria.forEach((criterion, cIndex) => {
        const val =
          criterion.value !== undefined
            ? String(criterion.value)
            : criterion.operator || 'Match';
        exits.push({
          id: `source-${index}-${cIndex}`,
          label: val,
          target: criterion.goto,
          isCriteria: true,
        });
      });
    } else {
      exits.push({
        id: `source-${index}`,
        label: route.signal || SIGNALS.NEXT,
        target: route.goto,
        isCriteria: false,
      });
    }
  });
  return exits;
};

export const getRouteStyle = (nodeType, label) => {
  const lowerLabel = label ? label.toLowerCase() : '';

  if (nodeType === NODE_TYPES.TRIGGER)
    return { stroke: COLORS.TRIGGER.stroke, bgClass: COLORS.TRIGGER.bgClass };
  if (label === 'INCOMING_MESSAGE')
    return {
      stroke: COLORS.INCOMING_MESSAGE.stroke,
      bgClass: COLORS.INCOMING_MESSAGE.bgClass,
    };

  if (lowerLabel.includes('fail') || lowerLabel.includes('timeout'))
    return {
      stroke: COLORS.FAIL_TIMEOUT.stroke,
      bgClass: COLORS.FAIL_TIMEOUT.bgClass,
    };
  if (
    lowerLabel.includes('sent') ||
    lowerLabel.includes('reply') ||
    lowerLabel.includes('success') ||
    lowerLabel === SIGNALS.MATCH.toLowerCase()
  )
    return {
      stroke: COLORS.SUCCESS_MATCH.stroke,
      bgClass: COLORS.SUCCESS_MATCH.bgClass,
    };
  if (lowerLabel.includes('no match') || lowerLabel.includes('criteria'))
    return {
      stroke: COLORS.NO_MATCH_CRITERIA.stroke,
      bgClass: COLORS.NO_MATCH_CRITERIA.bgClass,
    };

  return { stroke: COLORS.DEFAULT.stroke, bgClass: COLORS.DEFAULT.bgClass };
};

export const parseFlowData = flowData => {
  const nodes = [];
  const edges = [];

  if (!flowData || !flowData.nodes) return { nodes, edges };

  flowData.nodes.forEach(node => {
    let label = node.name || node.id;
    const styles = {};

    if (node.type === NODE_TYPES.TRIGGER) {
      styles.background = '#eff6ff';
      styles.borderColor = '#3b82f6';
      label = `Trigger: ${node.id}`;
    } else if (node.type === NODE_TYPES.SWITCH) {
      styles.background = '#fff7ed';
      styles.borderColor = '#f97316';
      label = `Switch: ${node.id}`;
    } else if (node.type === NODE_TYPES.SEND_MESSAGE) {
      styles.background = '#f0fdf4';
      styles.borderColor = '#22c55e';
      label = `Send Message: ${node.id}`;
    } else if (node.type === NODE_TYPES.SEND_MESSAGE_AND_WAIT) {
      styles.background = '#f0fdf4';
      styles.borderColor = '#22c55e';
      label = `Send Message (wait for reply): ${node.id}`;
    } else if (node.type === NODE_TYPES.MAKE_HTTP_REQUEST) {
      styles.background = '#faf5ff';
      styles.borderColor = '#a855f7';
      label = `HTTP Request: ${node.id}`;
    }

    nodes.push({
      id: node.id,
      type: 'custom',
      data: {
        label,
        original: node,
        nodeType: node.type,
      },
      position: node.ui?.position || { x: 0, y: 0 },
    });

    const exits = getNodeExitPoints(node);
    exits.forEach(exit => {
      if (exit.target) {
        const { stroke } = getRouteStyle(node.type, exit.label);

        edges.push({
          id: `e-${node.id}-${exit.target}-${exit.id}`,
          source: node.id,
          target: exit.target,
          sourceHandle: exit.id,
          type: 'draggable',
          animated: true,
          style: { stroke, strokeWidth: 2 },
          data: {
            label: exit.label,
          },
          markerMid: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: stroke,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 26,
            height: 26,
            color: stroke,
          },
        });
      }
    });
  });

  return { nodes, edges };
};

const SOURCE_HANDLE_REGEX = /^source-(\d+)(?:-(\d+))?$/;

function clearGotosInNode(node) {
  const routes = node.routes ?? [];
  for (let i = 0; i < routes.length; i += 1) {
    routes[i].goto = null;
    const criteria = routes[i].criteria ?? [];
    for (let j = 0; j < criteria.length; j += 1) {
      criteria[j].goto = null;
    }
  }
  return node;
}

/**
 * React Flow only stores edges; on save we sync route.goto / criterion.goto from
 * those edges so the backend receives the correct node connections.
 */
export function applyEdgesToNodeRoutes(nodes, edges) {
  if (!nodes?.length) return nodes;
  const edgesBySource = new Map();
  (edges || []).forEach(e => {
    if (e.source && e.sourceHandle != null && e.target) {
      const list = edgesBySource.get(e.source) ?? [];
      list.push({ sourceHandle: e.sourceHandle, target: e.target });
      edgesBySource.set(e.source, list);
    }
  });

  return nodes.map(node => {
    const nodeEdges = edgesBySource.get(node.id);
    if (!nodeEdges?.length) {
      return clearGotosInNode(JSON.parse(JSON.stringify(node)));
    }
    const nodeCopy = JSON.parse(JSON.stringify(node));
    clearGotosInNode(nodeCopy);
    nodeEdges.forEach(({ sourceHandle, target }) => {
      const m = String(sourceHandle).match(SOURCE_HANDLE_REGEX);
      if (!m) return;
      const routeIndex = parseInt(m[1], 10);
      const criterionIndex = m[2] != null ? parseInt(m[2], 10) : null;
      const routes = nodeCopy.routes ?? [];
      if (routeIndex < 0 || routeIndex >= routes.length) return;
      if (criterionIndex != null) {
        const criteria = routes[routeIndex].criteria ?? [];
        if (criterionIndex >= 0 && criterionIndex < criteria.length) {
          criteria[criterionIndex].goto = target;
        }
      } else {
        routes[routeIndex].goto = target;
      }
    });
    return nodeCopy;
  });
}
